import React, { useState } from 'react';
import { logEvent } from "firebase/analytics";
import { analytics } from "./firebaseConfig";
import './MapControls.css';

const MapControls = ({
    filter,
    onFilterChange,
    rinks,
    onLocateUser,
    onRinkSelect,
    isLocating,
    theme,
    toggleTheme,
    onZoomIn,
    onZoomOut,
    viewType = 'rink',
    onViewTypeChange
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0); // For keyboard navigation

    // Count open/closed rinks
    const openCount = rinks.filter(r => r.ouvert == 1).length;
    const closedCount = rinks.filter(r => r.ouvert != 1).length;
    const totalCount = rinks.length;

    // Normalize text (remove accents, special chars, whitespace)
    const normalizeText = (text) => {
        return text
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z0-9]/g, "");
    };

    // Filter rinks for search
    const searchResults = searchQuery.length > 0
        ? rinks.filter(r =>
            normalizeText(r.nom).includes(normalizeText(searchQuery))
        ).slice(0, 5) // Limit to 5 results
        : [];

    const handleSearchSelect = (rink) => {
        if (analytics) {
            logEvent(analytics, 'search_select', { rink_name: rink.nom });
        }
        onRinkSelect(rink);
        setSearchQuery('');
        setShowResults(false);
        setSelectedIndex(0);
    };

    const handleKeyDown = (e) => {
        if (!showResults || searchResults.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev => (prev + 1) % searchResults.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchSelect(searchResults[selectedIndex]);
        }
    };

    const handleFilterChange = (newFilter) => {
        if (analytics) {
            logEvent(analytics, 'select_content', {
                content_type: 'filter',
                item_id: newFilter
            });
        }
        onFilterChange(newFilter);
    };

    const handleLocateUser = () => {
        if (analytics) {
            logEvent(analytics, 'locate_user');
        }
        onLocateUser();
    };

    return (
        <div className="map-controls">
            {/* Top Bar with Brand and Search */}
            <div className="map-controls__top-bar">
                <div className="map-controls__brand-wrapper glass">
                    <div className="map-controls__brand">
                        <img
                            src="/logo2.png"
                            alt="RinkClic"
                            className="map-controls__logo"
                        />
                        <div className="map-controls__brand-text">
                            <h1 className="map-controls__title">RinkClic</h1>
                            <p className="map-controls__subtitle">
                                {viewType === 'trail' ? 'Pistes de ski de Montréal' : 'Patinoires de Montréal'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Stats counter */}
                <div className="map-controls__stats glass">
                    <div className="map-controls__stat">
                        <span className="map-controls__stat-value map-controls__stat-value--open">{openCount}</span>
                        <span className="map-controls__stat-label">Ouvertes</span>
                    </div>
                    <div className="map-controls__stat-divider"></div>
                    <div className="map-controls__stat">
                        <span className="map-controls__stat-value map-controls__stat-value--closed">{closedCount}</span>
                        <span className="map-controls__stat-label">Fermées</span>
                    </div>
                    <div className="map-controls__stat-divider"></div>
                    <div className="map-controls__stat">
                        <span className="map-controls__stat-value">{totalCount}</span>
                        <span className="map-controls__stat-label">Total</span>
                    </div>
                </div>

                <div className="map-controls__search-wrapper glass">
                    <div className="map-controls__search-container">
                        <input
                            type="text"
                            className="map-controls__search-input"
                            placeholder={viewType === 'trail' ? "Rechercher une piste..." : "Rechercher une patinoire..."}
                            value={searchQuery}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (searchQuery === '' && value !== '') {
                                    if (analytics) logEvent(analytics, 'search_initiate');
                                }
                                setSearchQuery(value);
                                setShowResults(true);
                                setSelectedIndex(0); // Reset selection on input change
                            }}
                            onFocus={() => {
                                setShowResults(true);
                                if (analytics) logEvent(analytics, 'search_focus');
                            }}
                            onKeyDown={handleKeyDown}
                        />
                        {showResults && searchResults.length > 0 && (
                            <ul className="map-controls__search-results">
                                {searchResults.map((rink, index) => (
                                    <li
                                        key={rink.nom}
                                        className={`map-controls__search-item ${index === selectedIndex ? 'map-controls__search-item--selected' : ''}`}
                                        onClick={() => handleSearchSelect(rink)}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    >
                                        <span className={`map-controls__filter-dot ${rink.ouvert == 1 ? 'map-controls__filter-dot--open' : 'map-controls__filter-dot--closed'}`}></span>
                                        {rink.nom}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>



            {/* Filter buttons */}
            <div className="map-controls__filters glass">
                <button
                    className={`map-controls__filter ${filter === 'all' ? 'map-controls__filter--active' : ''}`}
                    onClick={() => handleFilterChange('all')}
                >
                    Toutes
                </button>
                <button
                    className={`map-controls__filter map-controls__filter--open ${filter === 'open' ? 'map-controls__filter--active' : ''}`}
                    onClick={() => handleFilterChange('open')}
                >
                    <span className="map-controls__filter-dot map-controls__filter-dot--open"></span>
                    Ouvertes
                </button>
                <button
                    className={`map-controls__filter map-controls__filter--closed ${filter === 'closed' ? 'map-controls__filter--active' : ''}`}
                    onClick={() => handleFilterChange('closed')}
                >
                    <span className="map-controls__filter-dot map-controls__filter-dot--closed"></span>
                    Fermées
                </button>
                <div className="map-controls__stat-divider" style={{ margin: '0 8px', height: '24px' }}></div>
                <button
                    className="map-controls__filter"
                    onClick={toggleTheme}
                    title={theme === 'dark' ? "Mode clair" : "Mode sombre"}
                    style={{ padding: '8px 12px' }}
                >
                    {theme === 'dark' ? '☀️' : '🌙'}
                </button>
            </div>

            {/* Legend */}
            <div className="map-controls__legend glass">
                <h3 className="map-controls__legend-title">Légende</h3>
                <div className="map-controls__legend-items">
                    {viewType === 'trail' ? (
                        <>
                            <div className="map-controls__legend-item">
                                <svg width="20" height="25" viewBox="0 0 32 40" className="map-controls__legend-icon">
                                    <path d="M16 0C8.27 0 2 6.27 2 14c0 10.5 14 25 14 25s14-14.5 14-25C30 6.27 23.73 0 16 0z" fill="#dbeafe" stroke="#2563eb" strokeWidth="1.5"/>
                                    <circle cx="16" cy="13" r="8" fill="white" stroke="#3b82f6" strokeWidth="1.5"/>
                                    <g transform="translate(16, 13)" fill="#3b82f6">
                                        <polygon points="0,-5 -4,0 -2,0 -5,4 4,4 1,0 3,0" />
                                        <rect x="-1" y="4" width="2" height="2" />
                                    </g>
                                </svg>
                                <span>Piste ouverte</span>
                            </div>
                            <div className="map-controls__legend-item">
                                <svg width="20" height="25" viewBox="0 0 32 40" className="map-controls__legend-icon">
                                    <path d="M16 0C8.27 0 2 6.27 2 14c0 10.5 14 25 14 25s14-14.5 14-25C30 6.27 23.73 0 16 0z" fill="#fee2e2" stroke="#dc2626" strokeWidth="1.5"/>
                                    <circle cx="16" cy="13" r="8" fill="white" stroke="#ef4444" strokeWidth="1.5"/>
                                    <g transform="translate(16, 13)" fill="#ef4444">
                                        <polygon points="0,-5 -4,0 -2,0 -5,4 4,4 1,0 3,0" />
                                        <rect x="-1" y="4" width="2" height="2" />
                                    </g>
                                </svg>
                                <span>Piste fermée</span>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="map-controls__legend-item">
                                <svg width="20" height="25" viewBox="0 0 32 40" className="map-controls__legend-icon">
                                    <path d="M16 0C8.27 0 2 6.27 2 14c0 10.5 14 25 14 25s14-14.5 14-25C30 6.27 23.73 0 16 0z" fill="#dcfce7" stroke="#16a34a" strokeWidth="1.5"/>
                                    <circle cx="16" cy="13" r="8" fill="white" stroke="#22c55e" strokeWidth="1.5"/>
                                    <g transform="translate(16, 13)" fill="#22c55e">
                                        <line x1="0" y1="-4" x2="0" y2="4" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="-3.5" y1="-2" x2="3.5" y2="2" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="-3.5" y1="2" x2="3.5" y2="-2" stroke="#22c55e" strokeWidth="1.5" strokeLinecap="round"/>
                                    </g>
                                </svg>
                                <span>Patinoire ouverte</span>
                            </div>
                            <div className="map-controls__legend-item">
                                <svg width="20" height="25" viewBox="0 0 32 40" className="map-controls__legend-icon">
                                    <path d="M16 0C8.27 0 2 6.27 2 14c0 10.5 14 25 14 25s14-14.5 14-25C30 6.27 23.73 0 16 0z" fill="#fee2e2" stroke="#dc2626" strokeWidth="1.5"/>
                                    <circle cx="16" cy="13" r="8" fill="white" stroke="#ef4444" strokeWidth="1.5"/>
                                    <g transform="translate(16, 13)" fill="#ef4444">
                                        <line x1="0" y1="-4" x2="0" y2="4" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="-3.5" y1="-2" x2="3.5" y2="2" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="-3.5" y1="2" x2="3.5" y2="-2" stroke="#ef4444" strokeWidth="1.5" strokeLinecap="round"/>
                                    </g>
                                </svg>
                                <span>Patinoire fermée</span>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Map Actions (Locate + Zoom) */}
            <div className="map-controls__actions">
                <button className="map-controls__action-btn glass" onClick={handleLocateUser} title="Ma position">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2">
                        <circle cx="12" cy="12" r="7" />
                        <circle
                            cx="12" cy="12" r="3"
                            fill="#333333"
                            stroke="none"
                            className={isLocating ? "map-controls__locate-loading" : ""}
                        />
                        <line x1="12" y1="2" x2="12" y2="5" />
                        <line x1="12" y1="19" x2="12" y2="22" />
                        <line x1="2" y1="12" x2="5" y2="12" />
                        <line x1="19" y1="12" x2="22" y2="12" />
                    </svg>
                </button>
                <button className="map-controls__action-btn glass" onClick={onZoomIn} title="Zoomer">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </button>
                <button className="map-controls__action-btn glass" onClick={onZoomOut} title="Dézoomer">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#666666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </button>
            </div>

            {/* View Mode Toggle (bottom left) */}
            <button
                className="map-controls__mode-toggle glass"
                onClick={() => onViewTypeChange && onViewTypeChange(viewType === 'trail' ? 'rink' : 'trail')}
                title={viewType === 'trail' ? "Voir les patinoires" : "Voir les pistes de ski"}
            >
                <span className="map-controls__mode-icon">
                    {viewType === 'trail' ? '⛸️' : '⛷️'}
                </span>
            </button>

            {/* Footer */}
            <div className="map-controls__footer glass">
                <p className="map-controls__footer-text">
                    Fait avec ❤️ &{" "}
                    <a
                        href="https://buymeacoffee.com/zilongl"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="map-controls__support-link"
                        onClick={() => analytics && logEvent(analytics, 'support_click')}
                        title="Offrez-moi un pain!"
                    >
                        🥖(merci)
                    </a>
                    {" "}à Montréal
                </p>
            </div>
        </div>
    );
};

export default MapControls;
